import { useShallow } from 'zustand/react/shallow'

import { AstraHoverCard, AstraParagraph, day, getOnlyDate, humanizeDate, KeyboardKey, KeyboardShortcutPreview } from '~/astra'
import { ButtonTag } from '~/components'
import { StatusCode } from '~/store/slices/filterSlice'
import { selectPendingStatusesForCell } from '~/store/slices/surgeonSchedulerSlice'
import { useStore } from '~/store/store'
import { PractitionerStatusDefinition, usePractitionerScheduleStatuses, usePractitionerStatusDefinition } from '~/storm'

import { DatePractitionerCell } from '../../../shared'
import { createGetStatusStateFn, toggleStatus } from './toggleStatus'

type Props = {
    selectedCells: DatePractitionerCell[]
    statusCode: StatusCode
    autoFocus?: boolean
}

export const StatusTag = ({ selectedCells, statusCode, autoFocus = false }: Props) => {
    // Self-select only this status_code's slice for the selected cells. With Storm's
    // shallow-equality hook, this tag only re-renders when *its own* subset changes —
    // unrelated mutations to other status codes don't trigger a re-render.
    const scheduleStatuses = usePractitionerScheduleStatuses({
        where: {
            status_code: statusCode,
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })

    const pendingStatuses = useStore(
        useShallow(state =>
            selectedCells.flatMap(({ date, practitionerId }) =>
                selectPendingStatusesForCell(practitionerId, getOnlyDate(date))(state).filter(s => s.status_code === statusCode)
            )
        )
    )
    const minimalWithPendingStatuses = [...pendingStatuses, ...scheduleStatuses]
    const getStatusState = createGetStatusStateFn(selectedCells, minimalWithPendingStatuses)

    const status = usePractitionerStatusDefinition({ where: { code: statusCode } })

    function getTooltip(status?: PractitionerStatusDefinition) {
        if (selectedCells.length !== 1) return status?.title

        const scheduleStatus = scheduleStatuses.find(scheduleStatus => scheduleStatus.status_code === status?.code)
        return (
            <div className="flex flex-col items-center justify-center px-2 py-1">
                <div className="flex items-center gap-3">
                    {status?.title}
                    {status?.keyboard_shortcut && (
                        <KeyboardShortcutPreview
                            shortcut={{
                                keyboardKey: status.keyboard_shortcut as KeyboardKey,
                                hasShift: !!status.keyboard_has_shift,
                                hasMeta: !!status.keyboard_has_meta_key,
                            }}
                            variant="inverted"
                        />
                    )}
                </div>
                {scheduleStatus && (
                    <AstraParagraph className="text-inverted">{`Sist endret av ${scheduleStatus.updated_by} ${humanizeDate(day(scheduleStatus.updated_at))}`}</AstraParagraph>
                )}
            </div>
        )
    }

    return (
        <AstraHoverCard hoverContent={getTooltip(status)} noPadding placement="top" showArrow variant="inverted">
            <ButtonTag
                autoFocus={autoFocus}
                isLoading={pendingStatuses.length > 0}
                colorType={status?.color ?? 'gray'}
                fillType={getStatusState(statusCode)}
                onClick={() => toggleStatus(selectedCells, statusCode).catch(console.error)}
                label={status?.label}
            />
        </AstraHoverCard>
    )
}
